---
title: "Embedding jobs on your own site"
description: "Show your Chosen job openings on your own marketing site: direct links to specific jobs, and the public API for rendering your own jobs page."
category: "careers"
keywords:
  - "embed jobs on website"
  - "careers page API"
  - "link to job posting"
  - "jobs feed"
schema: "HowTo"
nav:
  section: "guides"
  order: 50
  parent: "/guides/careers"
---

If your marketing site has its own careers section, you don't have to maintain a second list of jobs. Link straight to your Chosen job pages, or pull the openings through the public API and render them yourself. Either way, you open and close jobs in one place — Chosen stays the source of truth.

## Link directly to a job

The simplest option, and it works today with no setup. Every open job has its own URL:

```
https://jobs.chosenhq.com/careers/<your-org-slug>/<public-job-id>
```

Link to it from anywhere — your site, a job board, a tweet, an email. The link is live as long as the job is Open and your careers page is on. When you close the job or set it to Internal, the link stops resolving. See [publishing jobs](/guides/careers/publish-jobs) for how status behaves.

You can also link to your full careers page at `/careers/<your-org-slug>`, where candidates browse and filter every open role.

<Callout type="info" title="Job links break if you change your slug">
A direct job link contains your org slug. Change the slug under **Career Page Settings** and every link you've posted elsewhere goes dead. Settle on a slug before you scatter links across the internet.
</Callout>

## Render your own jobs page with the API

To list openings inside your own site's design — your fonts, your layout, your filters — pull the job data programmatically and render it however you like.

Chosen has a public API at [developers.chosenhq.com](https://developers.chosenhq.com), authenticated with an API key. It can return your jobs as structured data that your marketing site builds its own UI from. Your front-end controls the look; Chosen owns the data.

A common setup: your site fetches the current openings, renders its own cards, and each card links to the Chosen job page where the candidate actually applies. You get a careers section that matches your brand without rebuilding the apply flow.

## Keep one source of truth

Whichever route you pick, manage jobs in Chosen and nowhere else.

- **Publish a job** — it appears on your careers page, and any API-fed list picks it up too.
- **Close a job** — it drops off the careers page, and your API-fed list stops returning it.

You never edit a job in two systems. Open and close roles in Chosen; your embedded list follows. A hand-maintained copy on your marketing site is the thing that goes stale and shows a closed role for three weeks — let the API list track Chosen instead.

## Limits

The public API key is an organization-level credential — treat it like one. Don't ship it in front-end code a visitor can read; fetch the job data from your own server and serve the rendered result to browsers. For setup, scopes, and the data the API returns, start at [developers.chosenhq.com](https://developers.chosenhq.com) and see [API keys](/reference/api-keys).
